import { useRef, useState } from "react";
import { Trash2, X } from "lucide-react";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { useTranslation } from "@ferrivia/i18n";
import {
  reachableGroupFields,
  type AnswerValue,
  type ItemFieldMap,
  type RepeatingQuestionDefinition,
} from "@ferrivia/api-contract";
import { useKeyboardScrollOffset } from "../hooks/useKeyboardScrollOffset";
import { GroupItemFields } from "./questions/GroupItemFields";
import s from "./ItemEditorScreen.module.css";

function hasValue(value: AnswerValue): boolean {
  switch (value.kind) {
    case "text":
    case "date":
      return value.value.trim() !== "";
    case "multi_choice":
      return value.value.length > 0;
    default:
      return true;
  }
}

export interface ItemEditorScreenProps {
  group: RepeatingQuestionDefinition;
  /** Empty for a new item. */
  initial: ItemFieldMap;
  mode: "add" | "edit";
  /** The item's wire index in edit mode, null in add mode. */
  itemIndex: number | null;
  invalidKeys: ReadonlySet<string>;
  onSave(fields: ItemFieldMap): void;
  /** Edit mode only. */
  onRemove?(): void;
  onCancel(): void;
}

/** One repeating-group item, added or edited on its own full-screen layer
    above the section. Edits stay in a local draft until Tallenna commits the
    whole item in one write; Peruuta, the X and hardware back all drop the
    draft. A layer of its own rather than a `Sheet`: the Sheet portals into
    `#root`, which sits under the questionnaire's own fixed z-index: 200. */
export function ItemEditorScreen({
  group,
  initial,
  mode,
  itemIndex,
  invalidKeys,
  onSave,
  onRemove,
  onCancel,
}: ItemEditorScreenProps) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const [draft, setDraft] = useState<ItemFieldMap>(() => new Map(initial));
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const keyboardHeight = useKeyboardScrollOffset(contentRef);

  const reachable = new Set(reachableGroupFields(group, draft).map((field) => field.key));
  // A field hidden again by its showIf after being filled in (stopped_at once
  // still_using flips back to Kyllä) is left out of the write, so a new item
  // never stores an answer the client can no longer see.
  const committed: ItemFieldMap = new Map([...draft].filter(([fieldKey]) => reachable.has(fieldKey)));
  const canSave = [...committed.values()].some(hasValue);

  const title =
    mode === "add"
      ? (copy.groupFor(group.key)?.editorTitle ?? copy.groupFor(group.key)?.label ?? group.key)
      : t("questionnaire.itemEditor.editTitle", "Muokkaa");

  function changeField(fieldKey: string, value: AnswerValue) {
    setDraft((prev) => new Map(prev).set(fieldKey, value));
  }

  return (
    <Screen className={s.root}>
      <div className={s.header}>
        <Text as="h1" size="lg" weight="bold" className={s.title}>
          {title}
        </Text>
        <button type="button" className={s.close} onClick={onCancel} aria-label={t("questionnaire.close", "Sulje")}>
          <X aria-hidden size={20} />
        </button>
      </div>

      <div className={s.scroll} ref={scrollRef}>
        <div ref={contentRef} className={s.fields}>
          <GroupItemFields
            group={group}
            item={draft}
            itemIndex={itemIndex}
            invalidKeys={invalidKeys}
            onFieldChange={changeField}
            scrollContainer={scrollRef}
          />
        </div>
        {keyboardHeight > 0 ? <div aria-hidden="true" style={{ height: keyboardHeight }} /> : null}
      </div>

      <div className={s.footer}>
        <Button className={s.save} disabled={!canSave} onClick={() => onSave(committed)}>
          {t("questionnaire.itemEditor.save", "Tallenna")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("questionnaire.itemEditor.cancel", "Peruuta")}
        </Button>
        {mode === "edit" && onRemove ? (
          <button type="button" className={s.removeButton} onClick={onRemove}>
            <Trash2 aria-hidden size={16} />
            {t("questionnaire.itemEditor.remove", "Poista")}
          </button>
        ) : null}
      </div>
    </Screen>
  );
}
